import { useTikTokData, useTikTokWatchlistHashtagData } from 'utils/hooks';
import { useTikTokWatchlistRelatedCategoryData } from 'utils/hooks/api';

export const useTikTokWatchlistMetricsData = ({
    categoryIds,
    brandIds,
    hashtagBrandIds,
    hashtagCategoryIds,
    relatedCategoryParentCategoryItems = [],
    relatedCategoryParentBrandItems = [],
    categoryPairs = [],
    brandPairs = [],
    country,
    placeholderMetricsDataByType,
    defaultInitialSortOption,
    week,
    year,
}) => {
    const baseTikTokDataParams = {
        week,
        year,
        order_by: defaultInitialSortOption.sortBy,
        ascending: defaultInitialSortOption.ascending,
        per_page: 500,
        page: 1,
    };

    const categoriesDataQuery = useTikTokData({
        ...baseTikTokDataParams,
        paginateBy: 'categories',
        categories: categoryIds,
        countries: country ? [country.id] : null,
        enabled: !!(country && categoryIds),
        placeholderData: placeholderMetricsDataByType.category.map((e) => e),
    });

    const brandsDataQuery = useTikTokData({
        ...baseTikTokDataParams,
        paginateBy: 'brands',
        brands: brandIds,
        countries: country ? [country.id] : null,
        enabled: !!(country && brandIds),
        placeholderData: placeholderMetricsDataByType.brand.map((e) => e),
    });

    const hashtagBrandsDataQuery = useTikTokWatchlistHashtagData({
        ...baseTikTokDataParams,
        hashtags: hashtagBrandIds.map(({ id }) => id),
        brands: hashtagBrandIds.map(({ brandId }) => brandId),
        countries: country ? [country.id] : null,
        enabled: !!(country && hashtagBrandIds.length),
        placeholderData: placeholderMetricsDataByType.hashtagBrand.map(
            (e) => e,
        ),
    });

    const hashtagCategoriesDataQuery = useTikTokWatchlistHashtagData({
        ...baseTikTokDataParams,
        hashtags: hashtagCategoryIds.map(({ id }) => id),
        categories: hashtagCategoryIds.map(({ categoryId }) => categoryId),
        countries: country ? [country.id] : null,
        enabled: !!(country && hashtagCategoryIds.length),
        placeholderData: placeholderMetricsDataByType.hashtagCategory.map(
            (e) => e,
        ),
    });

    // Keyword-matching cutover: a single pair-fetch replaces the three legacy
    // `useRelatedHashtagData` calls. The BE serves `(parent, related_category)`
    // matches from the new aggregate tables in one round trip.
    const relatedCategoryPairsQuery = useTikTokWatchlistRelatedCategoryData({
        categoryPairs,
        brandPairs,
        countries: country ? [country.id] : null,
        week,
        year,
        enabled: !!country,
        placeholderData: placeholderMetricsDataByType.relatedCategory?.map(
            (e) => e,
        ),
    });

    const allRelatedCategories = (
        relatedCategoryPairsQuery.data?.data || []
    ).map((row) => {
        // Prefer the parent name freshly resolved by the BE pair endpoint
        // (joins the parent Category/Brand table on every fetch). Falls
        // back to the snapshot stored on the watchlist item when present,
        // then to the joined brand name for legacy cases.
        const savedParentName = row.categoryId
            ? relatedCategoryParentCategoryItems.find(
                  ({ categoryId, relatedCategoryId }) =>
                      categoryId === row.categoryId &&
                      relatedCategoryId === row.relatedCategoryId,
              )?.parentName
            : relatedCategoryParentBrandItems.find(
                  ({ brandId, relatedCategoryId }) =>
                      brandId === row.brandId &&
                      relatedCategoryId === row.relatedCategoryId,
              )?.parentName;
        const parentName =
            row.parent_name || savedParentName || row.brandName || '';
        return {
            ...row,
            type: 'related-category',
            relatedType: row.related_type || 'category',
            relatedName: parentName,
            exportName: parentName ? `${parentName} - ${row.name}` : row.name,
        };
    });

    const metricsData = [
        ...(brandsDataQuery.data || []),
        ...(categoriesDataQuery.data || []),
        ...(hashtagBrandsDataQuery.data || []),
        ...(hashtagCategoriesDataQuery.data || []),
        ...allRelatedCategories,
    ];

    return {
        metricsData,
        brandsDataQuery,
        categoriesDataQuery,
        hashtagBrandsDataQuery,
        hashtagCategoriesDataQuery,
        relatedCategoryPairsQuery,
        allRelatedCategories,
    };
};
